// controllers
import AnimationController from '../../../assets/js/controllers/animation';

// static constants
import TimerEvent from '../../../bits/timer/timer-event';
import VideoEvent from '../../../assets/js/constants/video-event';

export default class GalleryVideos {

	static selector = 'gallery-videos';

	// class variables

	el = {};

	flags = {
		isPlaying: false
	};

	promises = {
		videoPlay: null
	};

	states = {
		activeItem: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			controls: container.querySelector(`.${this.constructor.selector}__controls`),
			videoPlayer: container.querySelector(`.${this.constructor.selector}__video`),
			videoPoster: container.querySelector(`.${this.constructor.selector}__video .image`),
			video: container.querySelector(`.${this.constructor.selector}__video video`)
		};

		this.initialize();
	}

	// initialize

	initialize() {
		this.#addEventListeners();
	}

	// add / remove event listeners

	#addEventListeners = () => {
		this.el.videoPlayer.addEventListener(VideoEvent.LOADING_META_DATA, this.#onVideoLoadedMetaData);
		this.el.videoPlayer.addEventListener(VideoEvent.PAUSE, this.#onVideoPlayerPause);
		this.el.videoPlayer.addEventListener(VideoEvent.PLAY, this.#onVideoPlayerPlay);
		this.el.videoPlayer.addEventListener(VideoEvent.SEEKING, this.#onVideoPlayerSeek);

		this.el.controls.addEventListener(TimerEvent.CHANGING, this.#onTimerItemChanging);
		this.el.controls.addEventListener(TimerEvent.PAUSING, this.#onTimerItemPausing);
		this.el.controls.addEventListener(TimerEvent.PLAYING, this.#onTimerItemPlaying);
		this.el.controls.addEventListener(TimerEvent.POLLING, this.#onTimerItemPolling);

		this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.POLL_STATUS));
	}

	#removeEventListeners = () => {
		this.el.videoPlayer.removeEventListener(VideoEvent.LOADING_META_DATA, this.#onVideoLoadedMetaData);
		this.el.videoPlayer.removeEventListener(VideoEvent.PAUSING, this.#onVideoPlayerPause);
		this.el.videoPlayer.removeEventListener(VideoEvent.PLAYING, this.#onVideoPlayerPlay);
		this.el.videoPlayer.removeEventListener(VideoEvent.SEEKING, this.#onVideoPlayerSeek);

		this.el.controls.removeEventListener(TimerEvent.CHANGING, this.#onTimerItemChanging);
		this.el.controls.removeEventListener(TimerEvent.PAUSING, this.#onTimerItemPausing);
		this.el.controls.removeEventListener(TimerEvent.PLAYING, this.#onTimerItemPlaying);
		this.el.controls.removeEventListener(TimerEvent.POLLING, this.#onTimerItemPolling);
	}

	// listener methods

	#onVideoLoadedMetaData = () => {
		const durationTime = this.el.video.duration * 1000;
		this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.ADD_TIME, { detail: durationTime }));
	}

	#onVideoPlayerPause = () => {
		// listen for commands to video player rather than status - otherwise will be caught in a loop
		this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.PAUSE));
	}

	#onVideoPlayerPlay = () => {
		// listen for commands to video player rather than status - otherwise will be caught in a loop
		this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.PLAY));
	}

	#onVideoPlayerSeek = (e) => {
		this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.SEEK, { detail: e.detail }));
	}

	#onTimerItemChanging = (e) => {
		const $target = e.detail.target;
		const willPlay = e.detail.isPlaying !== false;
		this.checkCurrentItem($target, willPlay);
	}

	#onTimerItemPolling = (e) => {
		const $target = e.detail.target;
		const willPlay = e.detail.isPlaying !== false;
		this.checkCurrentItem($target, willPlay);
	}

	#onTimerItemPausing = () => {
		this.pause();
	}

	#onTimerItemPlaying = () => {
		this.play();
	}

	//

	checkCurrentItem($target, willPlay = true) {
		if (this.states.activeItem !== $target) {
			const data = JSON.parse(decodeURIComponent($target.getAttribute('data-action')));
			if (data.type === 'image') {
				this.changeImage($target, data);
			} else if (data.type === 'video') {
				this.changeVideo($target, data, willPlay);
			}
		}
	}

	//

	changeImage($target, data) {
		// console.log('change image');
		if (this.states.activeItem !== $target) {
			this.resetMediaSources();

			// update picture (image)
			const $imgDesktop = document.createElement('img');
			$imgDesktop.setAttribute('src', data.image);
			$imgDesktop.setAttribute('alt', data.alt);
			this.el.videoPoster.appendChild($imgDesktop);

			const srcDesktop = document.createElement('source');
			srcDesktop.setAttribute('srcset', data.image);
			srcDesktop.setAttribute('media', '(max-width: 768px)');
			this.el.videoPoster.appendChild(srcDesktop);

			const srcTablet = document.createElement('source');
			srcTablet.setAttribute('srcset', data.imageTablet);
			srcDesktop.setAttribute('media', '(max-width: 480px)');
			this.el.videoPoster.appendChild(srcTablet);

			const srcMobile = document.createElement('source');
			srcMobile.setAttribute('srcset', data.imageMobile);
			srcDesktop.setAttribute('media', '(max-width: 479px)');
			this.el.videoPoster.appendChild(srcMobile);

			// set active state
			this.states.activeItem = $target;

			// load
			this.el.videoPlayer.dispatchEvent(new CustomEvent(VideoEvent.UNLOAD));

			// start timer
			AnimationController.one({
				update: () => true,
				render: () => {
					this.play();
				}
			});
		}
	}

	changeVideo($target, data, willPlay = true) {
		// console.log('change video');
		if (this.states.activeItem !== $target) {
			this.resetMediaSources();

			AnimationController.one({
				update: () => true,
				render: () => {
					// update poster
					const imgPosterDesktop = document.createElement('img');
					imgPosterDesktop.setAttribute('src', data.poster);
					imgPosterDesktop.setAttribute('alt', data.alt);
					this.el.videoPoster.appendChild(imgPosterDesktop);

					const srcPosterDesktop = document.createElement('source');
					srcPosterDesktop.setAttribute('srcset', data.poster);
					srcPosterDesktop.setAttribute('media', '(max-width: 768px)');
					this.el.videoPoster.appendChild(srcPosterDesktop);

					const srcPosterTablet = document.createElement('source');
					srcPosterTablet.setAttribute('srcset', data.posterTablet);
					srcPosterDesktop.setAttribute('media', '(max-width: 480px)');
					this.el.videoPoster.appendChild(srcPosterTablet);

					const srcPosterMobile = document.createElement('source');
					srcPosterMobile.setAttribute('srcset', data.posterMobile);
					srcPosterDesktop.setAttribute('media', '(max-width: 479px)');
					this.el.videoPoster.appendChild(srcPosterMobile);

					// update video
					const srcDesktop = document.createElement('source');
					srcDesktop.setAttribute('data-src', data.video);
					srcDesktop.setAttribute('data-src-desktop', true);
					this.el.video.appendChild(srcDesktop);

					const srcTablet = document.createElement('source');
					srcTablet.setAttribute('data-src', data.videoTablet);
					srcTablet.setAttribute('data-src-tablet', true);
					this.el.video.appendChild(srcTablet);

					const srcMobile = document.createElement('source');
					srcMobile.setAttribute('data-src', data.videoMobile);
					srcMobile.setAttribute('data-src-mobile', true);
					this.el.video.appendChild(srcMobile);

					// Set muted status
					if (data.isVideoMuted) {
						this.el.video.muted = true;
						this.el.videoPlayer.classList.add('is-muted', 'is-config-muted');
						this.el.videoPlayer.instance.flags.isMuted = true;
					} else {
						this.el.video.muted = false;
						this.el.video.volume = 1;
						this.el.videoPlayer.classList.remove('is-muted', 'is-config-muted');
						this.el.videoPlayer.instance.flags.isMuted = false;
					}

					// load
					this.el.videoPlayer.dispatchEvent(new CustomEvent(VideoEvent.RELOAD, { detail: { willPlay } }));
				}
			});

			this.states.activeItem = $target;
		}
	}

	resetMediaSources() {
		const sourcesImage = [...this.el.videoPoster.children];
		for (let i = 0; i < sourcesImage.length; i++) {
			const source = sourcesImage[i];
			source.remove();
		}

		const sourcesVideo = [...this.el.video.children];
		for (let i = 0; i < sourcesVideo.length; i++) {
			const source = sourcesVideo[i];
			source.remove();
		}

		this.el.video.load();
	}

	//

	pause() {
		this.flags.isPlaying = false;
		if (this.el.videoPlayer) {
			this.el.videoPlayer.dispatchEvent(new CustomEvent(VideoEvent.PAUSE));
		} else {
			this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.PAUSE));
		}
	}

	play() {
		this.flags.isPlaying = true;
		if (this.el.videoPlayer) {
			this.el.videoPlayer.dispatchEvent(new CustomEvent(VideoEvent.PLAY));
		} else {
			this.el.controls.dispatchEvent(new CustomEvent(TimerEvent.PLAY));
		}
	}

	// destroy

	destroy() {
		this.#removeEventListeners();
	}

}
